import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useFranchise } from '~/shared/utils/franchise';
import { useTranslation } from '~/shared/utils/translation';
import { useRouter } from 'next/router';
import cookie from 'js-cookie';
import { leverageNextLocaleCookieClient } from '~/services/utils/next-locale';
import { Button, Modal, Typography } from '~/shared/components';
import { Market } from './market/market.component';
import Check from '$icons/check.svg';
import Close from '$icons/close.svg';
import styles from './market-selector.module.css';

const LOCATION_DETECTION_COOKIE_KEY = 'LOCATION_DETECTED';

export const MarketSelector = memo(() => {
    const { franchiseMarket, activeMarket } = useFranchise();
    const { translate } = useTranslation();
    const { locale } = useRouter();
    const [selectorActive, setSelectorActive] = useState(false);
    const [barSelectorActive, setBarSelectorActive] = useState(false);
    const locationDetected = useMemo(
        () => cookie.get(LOCATION_DETECTION_COOKIE_KEY) !== 'false',
        []
    );

    const removeLocationCookie = useCallback(() => {
        cookie.remove(LOCATION_DETECTION_COOKIE_KEY);
    }, []);

    const onBarDismiss = useCallback(() => {
        setBarSelectorActive(false);
        removeLocationCookie();
    }, [removeLocationCookie]);

    const onSelectorDismiss = useCallback(() => setSelectorActive(false), [setSelectorActive]);

    useEffect(() => {
        if (!locationDetected) {
            setBarSelectorActive(true);
        }
    }, [locationDetected]);

    useEffect(() => {
        leverageNextLocaleCookieClient(locale);

        if (selectorActive && !locationDetected) {
            removeLocationCookie();
        }
    }, [locale, locationDetected, removeLocationCookie, selectorActive]);

    return (
        <>
            {barSelectorActive && (
                <div className={styles.marketBar}>
                    <Typography as="p">
                        {activeMarket?.countryName &&
                            translate('market.barNotice', { country: activeMarket.countryName })}
                    </Typography>
                    <div className={styles.marketBarContent}>
                        <Button
                            variant="quaternary"
                            size="small"
                            onClick={() => setSelectorActive(true)}
                        >
                            {activeMarket?.countryName}
                            <Check />
                        </Button>
                        <Button variant="secondary" size="small" onClick={onBarDismiss}>
                            {translate('general.continue')}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={onBarDismiss}
                            withIcon
                            className={styles.closeButton}
                        >
                            <Close title={translate('general.close')} />
                        </Button>
                    </div>
                </div>
            )}
            <Button
                variant="secondary"
                size="small"
                onClick={() => setSelectorActive(true)}
                hoverEffect
            >
                {activeMarket?.countryName}
                <Check />
            </Button>

            <Modal
                isOpen={selectorActive}
                onClose={onSelectorDismiss}
                variant="compact"
                headline={translate('market.marketSelectorHeadline')}
                doNotRemoveContentFromDOMWhenClosed
                removeBodyPadding
                scrollLock
            >
                <ul className={styles.marketList}>
                    {franchiseMarket?.markets?.map((market) => (
                        <li key={market.countryKey}>
                            <Market active={locale === market.locale} {...market} />
                        </li>
                    ))}
                </ul>
            </Modal>
        </>
    );
});
